import React, { useState, useContext, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import {List, Grid} from "react-feather"
import useSidebar from "../hooks/useSidebar";
import useSettingsState from "../hooks/useSettingsState";

import { UserSettingsContext } from "../contexts/UserSettingsContext";


import authenticatedFetch from "../utils/fetch"
import Task from "../utils/Task";
import TaskModal from "../utils/TaskModal";
import TaskBuilderModal from "../utils/TaskBuilderModal";
import TaskAssigneeSelect from "../utils/TaskAssigneeSelect";
import TaskPracticeSelect from "../utils/TaskPracticeSelect";
import TaskTable, { buildTaskColumns, compareTasksStarredOrderingDue, rawTaskStatusesWithShortLabels } from "../utils/TaskTable";


const Lane = ({ name, status, className='', showAddNewTask=false, children, reloadTaskLists }) => {
  const [show, setShow] = useState(false);

  const handleAddNewTask = () => setShow(true);
  const handleCancel = () => {
    setShow(false);
  }

  const handleSave = () => {
    setShow(false);
    reloadTaskLists();
  }

  return (

      <Card style={{minHeight: '200px'}}>
        <Card.Header>
          <Card.Title className={className}>{name}</Card.Title>
          {/* <h6 className="card-subtitle text-muted" >
            Nam pretium turpis et arcu. Duis arcu.
          </h6> */}
        </Card.Header>
        <Card.Body style={{marginTop: '-25px'}}>
          <div data-status={status}>{children}</div>
          {showAddNewTask ? 
          <>
            <div className="d-grid">
              <Button variant="primary" onClick={(e) => handleAddNewTask()}>+ Add new task</Button>
            </div> 
            <TaskModal show={show} onCancel={handleCancel} onSave={handleSave} />
          </>
          : ''
          }
          {status === 'COMPLETE' ?
          <>
          (Completed in past 30 days)
          </>
          : <></>
          }
        </Card.Body>
      </Card>
  );
};

const TaskList = ({taskList, taskToOpen, isLoaded, error, reloadTaskLists}) => {
  const columns = React.useMemo(
    () => buildTaskColumns({ reloadTaskLists, includeComplete: true, restrictActions: true }),
  []
  )

  const initialStatusFilter = rawTaskStatusesWithShortLabels.filter((status) => status.value !== "" && status.value !== "COMPLETE");

  const initialState = {
    sortBy: [
      { id: "task.starred", desc: false }, // starred first, in the user's ordering
      { id: "task.due", desc: false }
    ],
    filters: [
      { id: "task.status", value: initialStatusFilter }
    ]
  };

  return (
    <TaskTable columns={columns} data={taskList} initialState={initialState} getRowId={(row) => row.task.id.toString()} isLoaded={isLoaded} error={error} reloadTaskLists={reloadTaskLists} taskToOpen={taskToOpen}/>
  )
};

const Tasks = () => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [taskToOpen, setTaskToOpen] = useState(null);

  const [filteredAssignees, setFilteredAssignees] = useState([]);
  const [filteredPractices, setFilteredPractices] = useState([]);
  const [viewType, setViewType] = useSettingsState('taskViewType', 'grid') // TODO local storage is shared for all users on the computer.. OK for short term, but need to change to have preferences like this stored on server

  const [show, setShow] = useState(false);
  const [showTaskBuilder, setShowTaskBuilder] = useState(false);

  const { isH3Admin, authorities, setTaskAssigneeRefresh } = useContext(UserSettingsContext);
  const canEdit = isH3Admin || authorities.includes("TASK_EDIT");

  const { setBlockedBadge } = useSidebar();
  
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const taskId = searchParams.get("task");
    if (taskId && taskId !== '')
    {
      var parsed = parseInt(searchParams.get("task"));
      if (parsed && !isNaN(parsed))
      {
          setTaskToOpen(parsed);
          // Leave the param there so in the case of unauthorized it gets picked up by the destination param
      }
    }
  }, [searchParams]);

  const handleAssigneesChanged = (assignees) => {
    setFilteredAssignees(assignees);
  }

  const handlePracticesChanged = (practices) => {
    setFilteredPractices(practices);
  }

  function isFiltered(task) 
  {
    return isFilteredByAssignee(task) || isFilteredByPractice(task);
  }

  function isFilteredByAssignee(task) 
  {
    if (!filteredAssignees || filteredAssignees.length === 0)
    {
      return false;
    }

    if (!task.assignees || task.assignees.length === 0)
    {
      return true;
    }

    for (var i = 0; i < task.assignees.length; i++)
    {
      for (var j=0; j < filteredAssignees.length; j++)
      {
        if (task.assignees[i].userId === filteredAssignees[j].value)
        {
          return false;
        }
      }
    }

    return true;
  }

  function isFilteredByPractice(task) 
  {
    if (!filteredPractices || filteredPractices.length === 0)
    {
      return false;
    }

    if (!task.practiceId || task.practiceId === 0)
    {
      return true;
    }

    for (var j=0; j < filteredPractices.length; j++)
    {
      if (task.practiceId === filteredPractices[j].value)
      {
        return false;
      }
    }
  
    return true;
  }


  const handleAddNewTask = () => setShow(true);
  const handleTaskBuilder = () => setShowTaskBuilder(true);

  const handleCancel = () => {
    setShow(false);
  }
  
  const handleCancelTaskBuilder = () => {
    setShowTaskBuilder(false);
  }

  const handleSave = () => {
    setShow(false);
    setTaskToOpen(null);
    reloadTaskLists();
  }

  const handleTaskBuilderSave = () => {
    setShowTaskBuilder(false);
    reloadTaskLists();
  }

  const handleViewTypeChanged = (viewType) => {
    setTaskToOpen(null);
    setViewType(viewType);
  }

  useEffect(() => {
    authenticatedFetch({
      path: "task/revenueteam",
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });

    authenticatedFetch({
      path: "task/blocked",
      successHandler: function(result) {
        setBlockedBadge(result)
      }
    });
  }, [setBlockedBadge]);

  const reloadTaskLists = useCallback(() =>
  {
    setTaskToOpen(null);
    // setIsLoaded(false);
    authenticatedFetch({
      path: "task/revenueteam",
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
        setTaskAssigneeRefresh((prev) => prev + 1);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });

    authenticatedFetch({
      path: "task/blocked",
      successHandler: function(result) {
        setBlockedBadge(result)
      }
    });
  }, [setBlockedBadge, setTaskAssigneeRefresh])

  if (!isLoaded) {
    return (
    <React.Fragment>
        <Helmet title="Tasks" />
        <h1 className="h3 mb-3">Tasks</h1>
        <div>Loading...</div>
      </React.Fragment>
    );
  }
  else if (error || !data) {
    return (
      <React.Fragment>
          <Helmet title="Tasks" />
          <h1 className="h3 mb-3">Tasks</h1>
          <div>Something went wrong... please reload the page...</div>
        </React.Fragment>
    );
  }
  else {
    var showArchived=false;

    var tasksNotStarted = [];
    var tasksInProgress = [];
    var tasksBlocked = [];
    var tasksComplete = [];
    var taskList = [];

    if (viewType === "grid") {
    tasksNotStarted = data.filter(function(task) {
      if (task.status === 'CREATED' && (showArchived || !task.archived)) {
        if (isFiltered(task))
        {
          return false;
        }

        return true;
      }
      return false;
    }).sort(compareTasksStarredOrderingDue).map((task, i) => {
      return (
        <Task
          key={task.id}
          task={task}
          reloadTaskLists={reloadTaskLists}
          showModal={taskToOpen && taskToOpen === task.id}
          //attachments = {[1,2]}
        />
      )
    }, this);

    tasksInProgress = data.filter(function(task) {
      if (task.status === 'IN_PROGRESS' && (showArchived || !task.archived)) {
        if (isFiltered(task))
        {
          return false;
        }

        return true;
      }
      return false;
    }).sort(compareTasksStarredOrderingDue).map((task, i) => {
      return (
        <Task
          key={task.id}
          task={task}
          reloadTaskLists={reloadTaskLists}
          showModal={taskToOpen && taskToOpen === task.id}
          //attachments = {[1,2]}
        />
      )
    }, this);

    tasksBlocked = data.filter(function(task) {
      if ((task.status === 'BLOCKED' || task.status === 'BLOCKED_INTERNALLY' || task.status === 'BLOCKED_INTERNALLY_PROVIDER' || task.status === 'BLOCKED_SUBMITTED') && (showArchived || !task.archived)) {
        if (isFiltered(task))
        {
          return false;
        }

        return true;
      }
      return false;
    }).sort(compareTasksStarredOrderingDue).map((task, i) => {
      return (
        <Task
          key={task.id}
          task={task}
          reloadTaskLists={reloadTaskLists}
          showModal={taskToOpen && taskToOpen === task.id}
          //attachments = {[1,2]}
        />
      )
    }, this);

    tasksComplete = data.filter(function(task) {
      if (task.status === 'COMPLETE' && (showArchived || !task.archived)) {
        if (isFiltered(task))
        {
          return false;
        }

        return true;
      }
      return false;
    }).sort(function(taskA, taskB) {
      // We don't have a true completed date field, but sort completed tasks by updated date DESC
      if (taskA.updated < taskB.updated)
      {
        return 1;
      }
      if (taskA.updated > taskB.updated)
      {
        return -1;
      }

      return 0;
    }).map((task, i) => {
      return (
        <Task
          key={task.id}
          task={task}
          reloadTaskLists={reloadTaskLists}
          showDue={false}
          showModal={taskToOpen && taskToOpen === task.id}
          //attachments = {[1,2]}
        />
      )
    }, this);
    }
    else if (isLoaded)
    {
      taskList = data.filter(function(task) {
        if (showArchived || !task.archived) {
          if (isFiltered(task))
          {
            return false;
          }

          return true;
        }
        return false;
      }).map((task, i) => {
        return {task: task}
      }, this);
    }

    return (
      <React.Fragment>
        <Helmet title="Tasks" />
        <Container fluid className="p-0" style={{marginTop: "-20px"}}>
          {/* <Button variant="primary" className="float-end mt-n1">
            <FontAwesomeIcon icon={faPlus} /> New task
          </Button> */}
          <Row className="align-items-center" style={{marginBottom: "12px", rowGap: "10px"}}>
            <Col lg="auto">
              <h1 className="h3 mb-0 me-3">Tasks</h1>
            </Col>
            <Col lg="auto">
              {canEdit &&
              <>
                <Button variant="primary" size="sm" onClick={(e) => handleAddNewTask()}>+ Add new task</Button>
                <TaskModal show={show} onCancel={handleCancel} onSave={handleSave} />
                &nbsp;&nbsp;
                <Button variant="primary" size="sm" onClick={(e) => handleTaskBuilder()}>Task Builder</Button>
                <TaskBuilderModal show={showTaskBuilder} onHide={handleCancelTaskBuilder} onSave={handleTaskBuilderSave} />
              </>
              }
            </Col>
            <Col lg="auto" className="ms-lg-auto" style={{width: "240px"}}>
              <TaskAssigneeSelect onlyAssigneesOnCurrentTasks={true} handleAssigneesChanged={handleAssigneesChanged} selectedAssignees={filteredAssignees} placeholder="Filter by assignee..." compact/>
            </Col>
            <Col lg="auto" style={{width: "240px"}}>
              <TaskPracticeSelect handlePracticesChanged={handlePracticesChanged} selectedPractices={filteredPractices} compact/>
            </Col>
            <Col lg="auto">
              <Grid className={viewType === "grid" ? "text-success" : ""} style={{cursor: viewType === "grid" ? "default" : "pointer"}} onClick={(e) => handleViewTypeChanged('grid')}/>
              &nbsp;
              <List className={viewType === "list" ? "text-success" : ""} style={{cursor: viewType === "list" ? "default" : "pointer"}} onClick={(e) => handleViewTypeChanged('list')}/>
            </Col>
          </Row>
          {viewType === "grid" ?
          <Row>
            <Col sm="6" md="3">
              <Lane name="Inbox" status="CREATED" reloadTaskLists={reloadTaskLists} showAddNewTask={canEdit}>
                {tasksNotStarted}
              </Lane>
            </Col>
            <Col sm="6" md="3">
              <Lane name="In Progress"  status="IN_PROGRESS">
                {tasksInProgress}
              </Lane>
            </Col>
            <Col sm="6" md="3">
              <Lane name="Waiting" status="BLOCKED" className="alert alert-danger">
                {tasksBlocked}
              </Lane>
            </Col>
            <Col sm="6" md="3">
              <Lane name="Complete" status="COMPLETE">
                {tasksComplete}
              </Lane>
            </Col>
          </Row>
          : <>
              <TaskList taskList={taskList} taskToOpen={taskToOpen} isLoaded={isLoaded} error={error} reloadTaskLists={reloadTaskLists} />
            </>
          }
        </Container>
      </React.Fragment>
    );
  }
};

export default Tasks;
